import type { ComponentType } from 'react';
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router-dom';
import { SupportPage } from '@/features/support/pages/support-page';
import { CostsPage } from '@/features/costs/pages/costs-page';
import { FinancePage } from '@/features/finance/pages/finance-page';
import { FixedExpensesPage } from '@/features/finance/pages/fixed-expenses-page';
import { ReceivablePage } from '@/features/finance/pages/receivable-page';
import { OrdersPage } from '@/features/orders/pages/orders-page';
import { FiscalPage } from '@/features/fiscal/pages/fiscal-page';
import { AcceptInvitationPage } from '@/features/auth/pages/accept-invitation-page';
import { ForgotPasswordPage } from '@/features/auth/pages/forgot-password-page';
import { LoginPage } from '@/features/auth/pages/login-page';
import { ResetPasswordPage } from '@/features/auth/pages/reset-password-page';
import { TwoFactorPage } from '@/features/auth/pages/two-factor-page';
import { TwoFactorSetupPage } from '@/features/auth/pages/two-factor-setup-page';
import { RequireAuth } from '@/features/auth/require-auth';
import { MembersPage } from '@/features/organization/pages/members-page';
import { PrivacyPage } from '@/features/legal/pages/privacy-page';
import { type RouteHandle } from '@/components/shell/route-title';
import { AppShell } from './app-shell';
import { PlatformShell } from './platform-shell';

/**
 * Cada tela é baixada só quando é aberta: com todas importadas aqui, o site era um arquivo único
 * de 1,7 MB, lido inteiro antes da primeira tela. A rota carrega o módulo e usa o componente.
 */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return async () => ({ Component: (await load())[name] as ComponentType });
}

const router = createBrowserRouter([
  { path: '/entrar', element: <LoginPage /> },
  { path: '/entrar/codigo', element: <TwoFactorPage /> },
  { path: '/entrar/configurar-2fa', element: <TwoFactorSetupPage /> },
  { path: '/esqueci-senha', element: <ForgotPasswordPage /> },
  // Links novos levam o token depois do `#` (ADR-056); os antigos, no caminho, até vencerem.
  { path: '/redefinir-senha', element: <ResetPasswordPage /> },
  { path: '/redefinir-senha/:token', element: <ResetPasswordPage /> },
  { path: '/convite', element: <AcceptInvitationPage /> },
  { path: '/convite/:token', element: <AcceptInvitationPage /> },
  // Pública: é o endereço que as plataformas pedem no cadastro do aplicativo (ADR-089).
  { path: '/privacidade', element: <PrivacyPage /> },
  // Painel da plataforma (ADR-027): só a conta master; a sessão de empresa volta para "/".
  {
    element: (
      <RequireAuth scope="platform">
        <PlatformShell />
      </RequireAuth>
    ),
    children: [
      { path: '/plataforma', element: <Navigate to="/plataforma/saude" replace /> },
      {
        path: '/plataforma/saude',
        handle: { title: 'Saúde' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/health-page'), 'HealthPage'),
      },
      {
        path: '/plataforma/empresas',
        handle: { title: 'Empresas' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/platform/pages/organizations-page'),
          'OrganizationsPage',
        ),
      },
      {
        path: '/plataforma/empresas/:id',
        handle: { title: 'Empresa' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/organization-page'), 'OrganizationPage'),
      },
      {
        path: '/plataforma/ia',
        handle: { title: 'Visão geral' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/ai-overview-page'), 'AiOverviewPage'),
      },
      {
        path: '/plataforma/ia/prompts',
        handle: { title: 'Prompts' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/prompts-page'), 'PromptsPage'),
      },
      {
        path: '/plataforma/ia/prompts/:id',
        handle: { title: 'Prompt' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/prompt-page'), 'PromptPage'),
      },
      {
        path: '/plataforma/ia/funcoes',
        handle: { title: 'Funções e modelos' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/ai-functions-page'), 'AiFunctionsPage'),
      },
      {
        path: '/plataforma/ia/provedores',
        handle: { title: 'Provedores' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/ai-providers-page'), 'AiProvidersPage'),
      },
      {
        path: '/plataforma/politicas',
        handle: { title: 'Políticas de tarifa' } satisfies RouteHandle,
        lazy: page(() => import('@/features/platform/pages/policies-page'), 'PoliciesPage'),
      },
      {
        path: '/plataforma/politicas/mudancas/:id',
        handle: { title: 'Mudança de política' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/platform/pages/policy-change-page'),
          'PolicyChangePage',
        ),
      },
      {
        path: '/plataforma/conta',
        handle: { title: 'Minha conta' } satisfies RouteHandle,
        lazy: page(() => import('@/features/account/pages/account-page'), 'AccountPage'),
      },
    ],
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        path: '/',
        handle: { title: 'Visão geral' } satisfies RouteHandle,
        lazy: page(() => import('@/features/overview/pages/overview-page'), 'OverviewPage'),
      },
      {
        path: '/empresa/health-score',
        handle: { title: 'Health Score' } satisfies RouteHandle,
        lazy: page(() => import('@/features/score/pages/score-settings-page'), 'ScoreSettingsPage'),
      },
      {
        path: '/sistema',
        handle: { title: 'Estado do sistema' } satisfies RouteHandle,
        lazy: page(() => import('@/features/status/pages/status-page'), 'StatusPage'),
      },
      {
        path: '/acoes',
        handle: { title: 'Central de Ações' } satisfies RouteHandle,
        lazy: page(() => import('@/features/actions/pages/actions-page'), 'ActionsPage'),
      },
      {
        path: '/produtos',
        handle: { title: 'Catálogo' } satisfies RouteHandle,
        lazy: page(() => import('@/features/catalog/pages/products-page'), 'ProductsPage'),
      },
      {
        path: '/produtos/:id',
        handle: { title: 'Produto' } satisfies RouteHandle,
        lazy: page(() => import('@/features/catalog/pages/product-page'), 'ProductPage'),
      },
      {
        path: '/anuncios',
        handle: { title: 'Anúncios' } satisfies RouteHandle,
        lazy: page(() => import('@/features/listings/pages/listings-page'), 'ListingsPage'),
      },
      {
        path: '/anuncios/:id',
        handle: { title: 'Anúncio' } satisfies RouteHandle,
        lazy: page(() => import('@/features/listings/pages/listing-page'), 'ListingPage'),
      },
      {
        path: '/oportunidades',
        handle: { title: 'Oportunidades' } satisfies RouteHandle,
        lazy: page(() => import('@/features/growth/pages/opportunities-page'), 'OpportunitiesPage'),
      },
      {
        path: '/oportunidades/em-alta/:id',
        handle: { title: 'Em alta' } satisfies RouteHandle,
        lazy: page(() => import('@/features/growth/pages/trend-page'), 'TrendPage'),
      },
      {
        path: '/mercado-ads',
        handle: { title: 'Mercado Ads' } satisfies RouteHandle,
        lazy: page(() => import('@/features/growth/pages/ads-page'), 'AdsPage'),
      },
      {
        path: '/reputacao',
        handle: { title: 'Reputação' } satisfies RouteHandle,
        lazy: page(() => import('@/features/post-sale/pages/reputation-page'), 'ReputationPage'),
      },
      {
        path: '/estoque',
        handle: { title: 'Estoque' } satisfies RouteHandle,
        lazy: page(() => import('@/features/planning/pages/inventory-page'), 'InventoryPage'),
      },
      {
        path: '/compras',
        handle: { title: 'Compras' } satisfies RouteHandle,
        lazy: page(() => import('@/features/planning/pages/purchases-page'), 'PurchasesPage'),
      },
      {
        path: '/financeiro/caixa',
        handle: { title: 'Caixa previsto' } satisfies RouteHandle,
        lazy: page(() => import('@/features/planning/pages/cash-page'), 'CashPage'),
      },
      {
        path: '/resultados',
        handle: { title: 'Resultado das ações' } satisfies RouteHandle,
        lazy: page(() => import('@/features/pricing/pages/results-page'), 'ResultsPage'),
      },
      {
        path: '/promocoes',
        handle: { title: 'Promoções' } satisfies RouteHandle,
        lazy: page(() => import('@/features/pricing/pages/promotions-page'), 'PromotionsPage'),
      },
      {
        path: '/empresa/conta-completa',
        handle: { title: 'Conta completa' } satisfies RouteHandle,
        lazy: page(() => import('@/features/catalog/pages/readiness-page'), 'ReadinessPage'),
      },
      {
        path: '/automacoes',
        handle: { title: 'Automações' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/automations/pages/automations-page'),
          'AutomationsPage',
        ),
      },
      {
        path: '/inteligencia',
        handle: { title: 'Inteligência' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/intelligence/pages/intelligence-page'),
          'IntelligencePage',
        ),
      },
      { path: '/atendimento', element: <Navigate to="/atendimento/perguntas" replace /> },
      {
        path: '/atendimento/perguntas',
        handle: { title: 'Perguntas' } satisfies RouteHandle,
        element: <SupportPage kind="questions" />,
      },
      {
        path: '/atendimento/pos-venda',
        handle: { title: 'Pós-venda' } satisfies RouteHandle,
        element: <SupportPage kind="conversations" />,
      },
      {
        path: '/atendimento/perguntas/:id',
        handle: { title: 'Pergunta' } satisfies RouteHandle,
        lazy: page(() => import('@/features/support/pages/question-page'), 'QuestionPage'),
      },
      {
        path: '/atendimento/conversas/:id',
        handle: { title: 'Mensagens' } satisfies RouteHandle,
        lazy: page(() => import('@/features/support/pages/conversation-page'), 'ConversationPage'),
      },
      {
        path: '/empresa/atendimento',
        handle: { title: 'Prazos do atendimento' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/support/pages/support-settings-page'),
          'SupportSettingsPage',
        ),
      },
      {
        path: '/pedidos',
        handle: { title: 'Em aberto' } satisfies RouteHandle,
        element: <OrdersPage view="open" />,
      },
      {
        path: '/pedidos/consulta',
        handle: { title: 'Consultar' } satisfies RouteHandle,
        element: <OrdersPage view="all" />,
      },
      {
        path: '/pedidos/:id',
        handle: { title: 'Pedido' } satisfies RouteHandle,
        lazy: page(() => import('@/features/orders/pages/order-page'), 'OrderPage'),
      },
      {
        path: '/financeiro',
        handle: { title: 'Resultado' } satisfies RouteHandle,
        element: (
          <RequireAuth permission="finance.read">
            <FinancePage />
          </RequireAuth>
        ),
      },
      {
        path: '/financeiro/a-receber',
        handle: { title: 'A receber' } satisfies RouteHandle,
        element: (
          <RequireAuth permission="finance.read">
            <ReceivablePage />
          </RequireAuth>
        ),
      },
      {
        path: '/financeiro/despesas',
        handle: { title: 'Despesas fixas' } satisfies RouteHandle,
        element: (
          <RequireAuth permission="finance.read">
            <FixedExpensesPage />
          </RequireAuth>
        ),
      },
      {
        path: '/integracoes',
        handle: { title: 'Integrações' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/connections/pages/connections-page'),
          'ConnectionsPage',
        ),
      },
      {
        path: '/integracoes/retorno',
        handle: { title: 'Conectando a loja' } satisfies RouteHandle,
        lazy: page(
          () => import('@/features/connections/pages/oauth-return-page'),
          'OAuthReturnPage',
        ),
      },
      {
        path: '/empresa/custos',
        handle: { title: 'Custos e metas' } satisfies RouteHandle,
        element: (
          <RequireAuth permission="financials.read">
            <CostsPage />
          </RequireAuth>
        ),
      },
      {
        path: '/empresa/fiscal',
        handle: { title: 'Perfil fiscal' } satisfies RouteHandle,
        element: (
          <RequireAuth permission="revenue.read">
            <FiscalPage />
          </RequireAuth>
        ),
      },
      {
        path: '/empresa/notas-fiscais',
        handle: { title: 'Notas fiscais (SEFAZ)' } satisfies RouteHandle,
        lazy: page(() => import('@/features/nfe/pages/nfe-page'), 'NfePage'),
      },
      {
        path: '/conta',
        handle: { title: 'Minha conta' } satisfies RouteHandle,
        lazy: page(() => import('@/features/account/pages/account-page'), 'AccountPage'),
      },
      {
        path: '/empresa/membros',
        handle: { title: 'Membros' } satisfies RouteHandle,
        element: (
          <RequireAuth permission="organization.manage">
            <MembersPage />
          </RequireAuth>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
